/**
 * ChatNodeSettingsPanel - Collapsible side panel for ChatNode model settings
 *
 * Features:
 * - System Instructions textarea
 * - Model selector (opens dialog)
 * - Temperature slider (0-2)
 * - Media Resolution dropdown
 * - Thinking Level dropdown
 * - Tools section with toggles
 *
 * Design:
 * - Dark theme matching Google AI Studio style
 * - Smooth slide-in/out animation
 * - Compact but readable layout
 */

import { memo, useState, useCallback } from 'react';
import type {
  ChatNodeModelSettings,
  ThinkingLevel,
  MediaResolution,
} from '@deep-agents/shared-types';

// ============================================================================
// TYPES
// ============================================================================

interface ChatNodeSettingsPanelProps {
  /** Current model settings */
  settings: ChatNodeModelSettings;
  /** Called when any setting changes */
  onSettingsChange: (settings: ChatNodeModelSettings) => void;
  /** Whether the panel is open */
  isOpen: boolean;
  /** Called to close the panel */
  onClose: () => void;
  /** Called to open model selection dialog */
  onOpenModelSelect: () => void;
}

// ============================================================================
// ICONS
// ============================================================================

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 15l-6-6-6 6" />
  </svg>
);

const SparklesIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L9.19 8.63L2 9.24l5.46 4.73L5.82 21L12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z" />
  </svg>
);

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
  </svg>
);

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

/**
 * Toggle switch component
 */
interface ToggleSwitchProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  label: string;
  editButton?: boolean;
  onEdit?: () => void;
}

function ToggleSwitch({ enabled, onChange, label, editButton, onEdit }: ToggleSwitchProps) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      {/* Left side: label + optional edit - allow shrinking */}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className="text-sm text-slate-300 truncate">{label}</span>
        {editButton && (
          <button
            type="button"
            onClick={onEdit}
            className="text-xs text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0"
          >
            Edit
          </button>
        )}
      </div>
      {/* Toggle button - never shrink */}
      <button
        type="button"
        onClick={() => onChange(!enabled)}
        className={`
          relative w-10 h-5 rounded-full transition-colors duration-200 flex-shrink-0
          ${enabled ? 'bg-amber-500' : 'bg-slate-600'}
        `}
      >
        <span
          className={`
            absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm
            transition-transform duration-200
            ${enabled ? 'translate-x-5' : 'translate-x-0.5'}
          `}
        />
      </button>
    </div>
  );
}

/**
 * Dropdown select component
 */
interface SelectDropdownProps {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  label: string;
}

function SelectDropdown({ value, options, onChange, label }: SelectDropdownProps) {
  return (
    <div className="space-y-2">
      <label className="block text-sm text-slate-300">{label}</label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="
            nodrag nopan w-full px-3 py-2 text-sm text-white
            bg-slate-700 rounded-lg border border-slate-600
            appearance-none cursor-pointer
            hover:border-slate-500 focus:border-amber-500 focus:outline-none
          "
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
          <ChevronDownIcon />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ChatNodeSettingsPanel = memo(function ChatNodeSettingsPanel({
  settings,
  onSettingsChange,
  isOpen,
  onClose,
  onOpenModelSelect,
}: ChatNodeSettingsPanelProps) {
  // Local state for collapsible tools section
  const [toolsExpanded, setToolsExpanded] = useState(true);

  // -------------------------------------------------------------------------
  // HANDLERS - Update individual settings
  // -------------------------------------------------------------------------

  const updateSettings = useCallback(
    (partial: Partial<ChatNodeModelSettings>) => {
      onSettingsChange({ ...settings, ...partial });
    },
    [settings, onSettingsChange]
  );

  const updateTool = useCallback(
    (toolKey: keyof ChatNodeModelSettings['tools'], value: boolean) => {
      onSettingsChange({
        ...settings,
        tools: { ...settings.tools, [toolKey]: value },
      });
    },
    [settings, onSettingsChange]
  );

  // Get display name for current model
  const getModelDisplayName = (modelId: string) => {
    const modelNames: Record<string, string> = {
      'claude-sonnet-4-5-20250929': 'Claude Sonnet 4.5',
      'claude-opus-4-5-20250929': 'Claude Opus 4.5',
      'gemini-3-pro-preview': 'Gemini 3 Pro Preview',
      'gemini-3-flash-preview': 'Gemini 3 Flash Preview',
    };
    return modelNames[modelId] || modelId;
  };

  // -------------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------------

  return (
    <div
      className={`
        sticky top-0 self-start ml-2
        bg-slate-800 border border-slate-700 rounded-2xl
        transition-all duration-300 ease-out overflow-hidden
        ${isOpen ? 'w-72 opacity-100' : 'w-0 opacity-0 ml-0'}
      `}
      style={{
        zIndex: 10,
        height: 500, // Match the chat node height
        flexShrink: 0, // Prevent shrinking
      }}
    >
      <div className="flex flex-col h-full w-72">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <span className="text-sm font-medium text-white">Model Settings</span>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5 nodrag nopan">
          {/* System Instructions */}
          <div className="space-y-2">
            <label className="block text-sm text-slate-300">System instructions</label>
            <textarea
              value={settings.systemInstructions}
              onChange={(e) => updateSettings({ systemInstructions: e.target.value })}
              placeholder="Optional tone and style instructions for the model"
              className="
                nodrag nopan w-full h-20 px-3 py-2 text-sm text-white
                bg-slate-700/50 rounded-lg border border-slate-600
                placeholder:text-slate-500 resize-none
                hover:border-slate-500 focus:border-amber-500 focus:outline-none
              "
            />
          </div>

          {/* Model Selector Button */}
          <div className="space-y-2">
            <label className="block text-sm text-slate-300">Model</label>
            <button
              type="button"
              onClick={onOpenModelSelect}
              className="
                nodrag nopan w-full px-3 py-2.5 text-sm text-left text-white
                bg-slate-700 rounded-lg border border-slate-600
                hover:border-slate-500 hover:bg-slate-600/50
                transition-colors flex items-center justify-between
              "
            >
              <span className="flex items-center gap-2">
                <SparklesIcon />
                {getModelDisplayName(settings.modelId)}
              </span>
              <ChevronDownIcon />
            </button>
          </div>

          {/* Temperature Slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm text-slate-300">Temperature</label>
              <input
                type="number"
                value={settings.temperature}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  if (!isNaN(val) && val >= 0 && val <= 2) {
                    updateSettings({ temperature: val });
                  }
                }}
                step="0.1"
                min="0"
                max="2"
                className="
                  nodrag nopan w-14 px-2 py-1 text-sm text-white text-center
                  bg-slate-700 rounded border border-slate-600
                  focus:border-amber-500 focus:outline-none
                "
              />
            </div>
            <input
              type="range"
              value={settings.temperature}
              onChange={(e) => updateSettings({ temperature: parseFloat(e.target.value) })}
              min="0"
              max="2"
              step="0.1"
              className="nodrag nopan w-full h-1 bg-slate-600 rounded-lg appearance-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-4
                [&::-webkit-slider-thumb]:h-4
                [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-white
                [&::-webkit-slider-thumb]:shadow-md
                [&::-webkit-slider-thumb]:cursor-pointer
              "
            />
          </div>

          {/* Media Resolution */}
          <SelectDropdown
            label="Media resolution"
            value={settings.mediaResolution}
            onChange={(v) => updateSettings({ mediaResolution: v as MediaResolution })}
            options={[
              { value: 'auto', label: 'Auto' },
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' },
            ]}
          />

          {/* Thinking Level */}
          <SelectDropdown
            label="Thinking level"
            value={settings.thinkingLevel}
            onChange={(v) => updateSettings({ thinkingLevel: v as ThinkingLevel })}
            options={[
              { value: 'none', label: 'None' },
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' },
            ]}
          />

          {/* Tools Section */}
          <div className="pt-2 border-t border-slate-700">
            <button
              type="button"
              onClick={() => setToolsExpanded(!toolsExpanded)}
              className="flex items-center justify-between w-full py-2 text-sm text-slate-300 hover:text-white transition-colors"
            >
              <span>Tools</span>
              {toolsExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </button>

            {toolsExpanded && (
              <div className="space-y-1">
                <ToggleSwitch
                  label="Structured outputs"
                  enabled={settings.tools.structuredOutputs}
                  onChange={(v) => updateTool('structuredOutputs', v)}
                  editButton
                />
                <ToggleSwitch
                  label="Code execution"
                  enabled={settings.tools.codeExecution}
                  onChange={(v) => updateTool('codeExecution', v)}
                />
                <ToggleSwitch
                  label="Function calling"
                  enabled={settings.tools.functionCalling}
                  onChange={(v) => updateTool('functionCalling', v)}
                  editButton
                />
                <div className="py-2">
                  <div className="flex items-center justify-between gap-3">
                    {/* Left side - allow shrinking */}
                    <div className="min-w-0 flex-1">
                      <span className="text-sm text-slate-300 block truncate">Grounding with Google Search</span>
                      <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        Source: <SearchIcon /> Google Search
                      </p>
                    </div>
                    {/* Toggle - never shrink */}
                    <button
                      type="button"
                      onClick={() => updateTool('webSearchGrounding', !settings.tools.webSearchGrounding)}
                      className={`
                        relative w-10 h-5 rounded-full transition-colors duration-200 flex-shrink-0
                        ${settings.tools.webSearchGrounding ? 'bg-amber-500' : 'bg-slate-600'}
                      `}
                    >
                      <span
                        className={`
                          absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm
                          transition-transform duration-200
                          ${settings.tools.webSearchGrounding ? 'translate-x-5' : 'translate-x-0.5'}
                        `}
                      />
                    </button>
                  </div>
                </div>
                <ToggleSwitch
                  label="URL context"
                  enabled={settings.tools.urlContext}
                  onChange={(v) => updateTool('urlContext', v)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
